import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Currency formatting utilities
const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  AUD: "A$",
  CAD: "C$",
};

export function getCurrencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Formats a monetary amount for display.
 *
 * `amount` must be in the currency's minor unit (pence/cents — e.g. `4500`
 * for £45.00), matching how amounts flow through this app: the API returns
 * raw stored pence (see `WidgetCustomerUserPurchases.controller.js`), and
 * every other money formatter in this codebase already divides by 100
 * before display (`BasketCheckoutOverlay`'s local `formatPrice`,
 * `GiftcardCheckoutOverlay/types.ts`, and the hosted widget's
 * `Number__Currency`). All current call sites of this function pass pence.
 */
export function formatCurrency(amount: number, currency: string): string {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${(amount / 100).toFixed(2)}`;
}

/**
 * Console-safe, one-line description of an error — for any logging these
 * widgets do.
 *
 * NEVER pass a raw error object to `console.*` from here. Webcomponents run
 * inside THIRD-PARTY partner websites, and a great many of those run
 * session-replay / error-monitoring scripts (Sentry, LogRocket, Hotjar, …)
 * that serialise every `console.error` argument and ship it to their own
 * servers. An `AxiosError` — which is what the shared checkout lib hands to
 * `CheckoutApiConfig.onError` — carries `.config.headers.Authorization` (the
 * signed-in customer's live bearer token) and `.config.data` (the request
 * body, e.g. the buyer's email), so logging the object itself hands the
 * customer's session and PII to whatever the partner happens to have
 * installed, outside Baluu's control.
 *
 * Returns the message only, plus the HTTP status when the error carries one:
 * a plain number is safe to expose and is the single most useful thing for
 * diagnosing a failing endpoint.
 */
export function describeErrorForLog(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const status = (
    error as { response?: { status?: unknown } } | null | undefined
  )?.response?.status;
  return typeof status === "number" ? `${message} (HTTP ${status})` : message;
}

